import * as React from 'react';
import { AddDocumentItemOptions } from 'app/infrastructure/ui/form/types';
import { DialogExpander } from 'ui/common/dialog-expander/dialog-expander';
import { OnClickOutside } from 'ui/common/on-click-outside';
import { ButtonPrimary, Textbox } from 'ui';
import { ValidationLevel } from 'ui/common';
import { withOpenState, OpenStateInjectedProps } from 'ui/common/enhancers';
import { AddButton } from 'ui';
import { isValidUrl } from 'app/infrastructure/validation/url'; 
import { MdAdd } from 'react-icons/md';
import styles from './attach-link-selector.scss';
import { keyCodeTypes } from 'ui/common/key-code-types';
import { Handlers, HotKeys, KeyMap } from 'ui/common/hotkeys';

interface AttachLinkSelectorTranslationsProps {
  textAddItem?: string;  
  textLinkTitle?: string;
  textLinkUrl?: string;
  textDone?: string;
  textInvalidUrlMessage?: string;
  placeholderLinkUrl?: string;
  textRequiredError?: string;  
}

export interface AttachLinkSelectorProps extends AttachLinkSelectorTranslationsProps{   
  readOnly?: boolean;
  required?: boolean;  
  onAddDocumentItem?: (selectAddedItem?: boolean, options?: AddDocumentItemOptions) => void;  
  valueFormatter?: (title: string, linkUrl: string) => any;
  onSelectDocumentItem?: (path?: any) => void;
  details?: () => void;
}

interface AttachLinkSelectorState {
  inputTitle: string;
  inputLinkUrl: string;
  invalidUrl: boolean;
  requiredErrorTitle?: boolean;
  requiredErrorLink?: boolean;
}

export class AttachLinkSelectorComponent 
                    extends React.PureComponent<AttachLinkSelectorProps & OpenStateInjectedProps, AttachLinkSelectorState>  {
  private keyMap: KeyMap;
  private handlers: Handlers;
  public constructor(props: AttachLinkSelectorProps & OpenStateInjectedProps) {
    super(props);    
    this.state = {inputTitle: '', inputLinkUrl: '', invalidUrl: false, requiredErrorTitle: false, requiredErrorLink: false};
    
    this.keyMap = {
      onEnter: [keyCodeTypes.Enter],
      onEsc: [keyCodeTypes.Esc]
    };
    
    this.handlers = {
      onEnter: () => this.addLinkItem(),
      onEsc: () => this.closeDialog()
    };
  }

  private closeDialog = () => {     
    const {isOpen, close} = this.props;
    if (!isOpen) {
      return;
    }

    this.setState({
      ...this.state,
      ...{
          inputTitle: '', 
          inputLinkUrl: '',
          invalidUrl: false,
          requiredErrorTitle: false,
          requiredErrorLink: false       
      }
    }, () => close());
  };

  private onChangeTitle = (value: string) => {    
    if(this.isValueEmpty(value)) {
      this.setState({ inputTitle: value, requiredErrorTitle: true});
      return;
    }  
    this.setState({inputTitle: value, requiredErrorTitle: false});
  };

  private onChangeLinkUrl = (value: string) => {
    if(this.isValueEmpty(value)) {
      this.setState({ inputLinkUrl: value, requiredErrorLink: true});
      return;
    }      
    this.setState({inputLinkUrl: value, invalidUrl: false, requiredErrorLink: false});    
  };

  private isValueEmpty = (value: string): boolean => {    
    return !(value.trim().length);
  };

  private onBlurLinkUrl = (value: string)=> {
    if(!isValidUrl(value)) {
      this.setState({invalidUrl: true});
      return false;    
    }
    this.setState({inputLinkUrl: value, invalidUrl: false});
    return true;
  };
  
  private addLinkItem = () => {
    const {inputTitle, inputLinkUrl} = this.state;    
    const allowAddingLinkItem = isValidUrl(inputLinkUrl) && !this.isValueEmpty(inputTitle);

    this.isValueEmpty(inputTitle) && this.setState({requiredErrorTitle: true});
    this.isValueEmpty(inputLinkUrl) && this.setState({requiredErrorLink: true});
    !isValidUrl(inputLinkUrl.trim()) && this.setState({invalidUrl: true});
      
    if(this.props.onAddDocumentItem && allowAddingLinkItem) {      
      const args =  this.props.valueFormatter 
                    && this.props.valueFormatter(this.state.inputTitle, this.state.inputLinkUrl);
      this.props.onAddDocumentItem(false, { initialValues: args } as AddDocumentItemOptions);
      this.closeDialog();      
    }    
  };

  private  addSelectorContents = () => {
    const { textDone, textLinkTitle, textLinkUrl, textInvalidUrlMessage, placeholderLinkUrl, textRequiredError} = this.props;
    const {inputTitle, inputLinkUrl, invalidUrl, requiredErrorTitle, requiredErrorLink} = this.state;
    const labelLinkTitle = {visible: true, text: textLinkTitle, required: true};
    const labelLinkUrl = {visible: true, text: textLinkUrl, required: true};    
    const errorParamsInvalidUrl = {level: ValidationLevel.error,
      messages: [{level: ValidationLevel.error, message: `${textInvalidUrlMessage}`}]
    };  
    const errorParamsRequired = {level: ValidationLevel.error,
      messages: [{level: ValidationLevel.error, message: `${textRequiredError}`}]
    };
    const validationTitle = this.isValueEmpty(inputTitle) && requiredErrorTitle && errorParamsRequired;
    const validationLinkUrl = (this.isValueEmpty(inputLinkUrl) && requiredErrorLink) && errorParamsRequired 
                               || invalidUrl && errorParamsInvalidUrl; 
    
    const titleTextarea = <Textbox 
                            componentId={'link-title'}
                            className={`${'input-link-title'}`}                            
                            value={inputTitle}
                            onChange={this.onChangeTitle}
                            label={labelLinkTitle}                            
                            maxLength={4000}                            
                            validation={validationTitle}                           
                          />;

    const linkUrlTextarea = <Textbox 
                              componentId={'link-url'}
                              className={`${'input-link-url'}`}                              
                              value={inputLinkUrl}                              
                              onChange={this.onChangeLinkUrl}
                              onBlur={this.onBlurLinkUrl}
                              label={labelLinkUrl}
                              validation={validationLinkUrl}
                              placeholder={placeholderLinkUrl}                              
                              maxLength={4000}                              
                            />;        
    
    const addButton = <div className="d-flex justify-content-end">
                        <ButtonPrimary onClick={this.addLinkItem} >
                          {textDone}
                        </ButtonPrimary>
                      </div>;
                          
    return(<>
             {titleTextarea}
             {linkUrlTextarea}
             {addButton}                             
           </>);
  };

  private renderSelectDialog() {  
    
    return (
      <DialogExpander selectExpander={true} onClose={this.closeDialog} hasFooter={false}>                
        {this.props.onAddDocumentItem && this.addSelectorContents()}              
      </DialogExpander>
    );
  }

  public render() {
    const {
      open,    
      readOnly, 
      required,
      isOpen,
      textAddItem    
    } = this.props;

    if (readOnly) {
      return null;
    }

    const addButton = <AddButton icon={MdAdd} onClick={open} required={required}>
                        {textAddItem}
                      </AddButton>;
  
    return isOpen ? 
      
      <OnClickOutside onClickOutside={this.closeDialog}>
        <HotKeys keyMap={this.keyMap} handlers={this.handlers}>    
          <div className={styles.attachLinkSelector}>   
            {this.props.onAddDocumentItem && addButton}               
            {this.renderSelectDialog()}      
          </div>    
        </HotKeys>
      </OnClickOutside>
      
      :

      <div className={styles.attachLinkSelector}>   
        {this.props.onAddDocumentItem && addButton}  
      </div>;  
  }
}

const c = withOpenState()(AttachLinkSelectorComponent);

export { c as AttachLinkSelector };
